import queryString from 'query-string';
import moduleInit from '@utilities/module-init';
import Events from '@utilities/events';
import API from '@utilities/api';
import updateDom from '@utilities/update-dom';
import updateGtmListingFiltersEvents from '@utilities/updateGtmListingFiltersEvents';
import updateGtmListingSortEvents from '@utilities/updateGtmListingSortEvents';
import ScrollTo from "@utilities/scroll-to";

const JS_HOOK_REFINEMENTS_FILTER = '[js-hook-refinement-filter-context]';
const JS_HOOK_REFINEMENTS_BAR = 'js-hook-refinement-bar';
const JS_HOOK_REFINEMENT_UPDATE = 'js-hook-refinement-update';
const JS_HOOK_PAGED_NAVIGATION = '[js-hook-paged-navigation]';
const JS_HOOK_SCROLL_LIST_MODAL = '[js-hook-refinement-scroll-modal]';

const MODAL_ID = 'refinement-modal';

class Refinements {
  constructor(element) {
    this.refinements = element;
    this.refinementFilters = document.querySelector(JS_HOOK_REFINEMENTS_FILTER);
    this.url = '';
    this.refinementUpdates = [...document.querySelectorAll(`[${JS_HOOK_REFINEMENT_UPDATE}]`)];
    this.pagedNavigationId = this.refinements.querySelector(JS_HOOK_PAGED_NAVIGATION)?.dataset.id;

    this.bindEvents();
  }

  /**
   * Update the DOM of chips
   * @param {HTMLElement} content - DOM element
   */
  static updateModal(content) {
    updateDom(content, JS_HOOK_REFINEMENTS_BAR);
  }

  static updateInteractiveElements(content) {
    const interactiveElements = {
      [JS_HOOK_REFINEMENTS_BAR]: Refinements.updateModal,
    };

    Object.keys(interactiveElements).forEach((selector) => {
      interactiveElements[selector](content);
    });
  }

  static rebindEvents() {
    // Fire Event to Update Images
    Events.$trigger('lazyimage::update');

    // Bind All Events
    Events.$trigger('events::dom-reinit');

    // Bind load buttons
    Events.$trigger('product-grid::rebind-events');
  }

  initModules(scrollTop = null, scrollList = null) {

    moduleInit.async('[js-hook-dropdown]', () =>
      import(/* webpackChunkName: "js/Dropdown" */ '@components/dropdown'),
    );

    moduleInit.async('[js-hook-refinement-filter]', () =>
      import(/* webpackChunkName: "js/RefinementFilter" */ '@components/refinement-filter'),
    );

    moduleInit.async('[js-hook-paged-navigation]',
      () =>
        import(/* webpackChunkName: "js/PagedNavigation" */ '@components/paged-navigation'),
      {
        viewID: this.pagedNavigationViewID,
        scrollTop,
        scrollList
      }
    );

    moduleInit.async('[js-hook-sort-select-box]', () =>
      import(/* webpackChunkName: "js/SortSelectBox" */ '@components/sort-select-box'),
    );

    moduleInit.async('[js-hook-pagination]', () =>
      import(/* webpackChunkName: "js/Pagination" */ '@components/pagination'),
    );

    moduleInit.async('[js-hook-custom-select-box]', () =>
      import(/* webpackChunkName: "js/CustomSelectBox" */ '@components/custom-select-box'),
    );

    moduleInit.async('[js-hook-pagination]', () =>
      import(/* webpackChunkName: "js/Pagination" */ '@components/pagination'),
    );

    // Bind Modal Events
    Events.$trigger('modal::bind-buttons', { data: { id: MODAL_ID } });
  }

  /**
   * Replace static DOM elements
   *
   * @param {Object} content - DOM element
   */
  updateStaticElements(content) {
    this.refinementUpdates.forEach((selector) => {
      const hookName = selector.getAttribute(JS_HOOK_REFINEMENT_UPDATE);

      setTimeout (() => {
        updateDom(content, `[${JS_HOOK_REFINEMENT_UPDATE}=${hookName}]`);
      }, 0); // don't ask, weird fix for weird safari iphone bug
    });

    Events.$trigger('listing::new-page', {});
  }

  /**
   * Parse Axios results and updated select DOM elements
   *
   * @param {string} response - Axios response HTML code
   */
  getDataFromFilterSuccess(response) {
    Events.$trigger('loader::hide');

    // Parse using DOMParser
    const content = new DOMParser().parseFromString(response, 'text/html').body;

    // Update static DOM elements that do not have interaction
    this.updateStaticElements(content);

    // Update interactive DOM elements
    Refinements.updateInteractiveElements(content);

    setTimeout (() => {
      // Scroll if needed
      if (this.filterId && (this.scrollTop !== null)) {
        const filterList = document.getElementById(this.filterId);
        const filterScrollListModal = filterList ? filterList.querySelector(JS_HOOK_SCROLL_LIST_MODAL) : null;

        if (filterList && filterScrollListModal) {
          this.initModules(this.scrollTop, filterScrollListModal);
        } else {
          this.initModules();
        }
      } else {
        this.initModules();
      }

      //   Bind events again to new DOM elements
      Refinements.rebindEvents();

      // Updates Gtm Listing Filters Events (GTM listing_filter event)
      updateGtmListingFiltersEvents();

      // Updates Gtm Listing Sort Events (GTM listing_sort event)
      updateGtmListingSortEvents();

    }, 0); // don't ask, weird fix for weird safari iphone bug

    if (this.scrollTop) {
      ScrollTo.scrollTo(this.refinementFilters, 500, 150);
    }
  }

  /**
   * Retrieve data from filter
   * @param {Object} data - { url, params } for API
   * @param pushState boolean - Determines if history is replaced or pushed
   */
  getDataFromFilter(data, pushState) {
    if (!data) return;
    Events.$trigger('loader::show');

    this.url = data.url;

    const { query, url: newUrl } = queryString.parseUrl(this.url);
    const bodyObj = {
      selectedUrl: this.url,
      ...query,
    };

    this.filterId = data.filterId;

    this.scrollTop = data.scrollTop;

    API.get(newUrl, bodyObj)
      .then((response) => this.getDataFromFilterSuccess(response.data, pushState))
      .catch(() => Events.$trigger('loader::hide'));
  }

  /**
   * Bind all general events
   */
  bindEvents() {
    Events.$on(`pagedNavigation[${this.pagedNavigationId}]::changedView`, (e, data) => {
      this.pagedNavigationViewID = data.viewID;
    });

    Events.$on(`modal[${MODAL_ID}]::open`, () => {
      Events.$trigger(`pagedNavigation[${this.pagedNavigationId}]::changeView`, {
        data: {
          viewID: 'refinement-landing',
          animate: false
        }

      });
    });

    Events.$on('history::refinements-update', (_, data) => {
      const url = data?.state?.url || window.location.href
      const scrollTop = data?.state?.scrollTop;
      const filterId = data?.state?.filterId;

      this.getDataFromFilter({url, scrollTop, filterId}, false)
    })
  }
}

export default Refinements;
